import React from 'react';
import { Helmet } from 'react-helmet';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';

const About = () => {
  return (
    <div className="about-page pt-24 pb-16">
      <SEO 
        title="About Cosmik Drones | Josh Kolonia - Lehigh Valley FAA Drone Expert"
        description="Meet Josh Kolonia, Lehigh Valley's FAA-certified drone expert and owner of Cosmik Drones. Specializing in aerial photography & FPV, and a proud member of the Lehigh Valley Chamber of Commerce."
        canonicalUrl="https://cosmikdrones.com/about"
      />
      <SchemaMarkup 
        type="Person"
        data={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Josh Kolonia",
          "url": "https://cosmikdrones.com/about",
          "image": "https://cosmikdrones.com/images/Skirmish%20Profile.png", // Assuming this is a good profile image of Josh
          "jobTitle": "Owner & FAA-Certified Drone Pilot",
          "worksFor": {
            "@type": "Organization",
            "name": "Cosmik Drones",
            "url": "https://cosmikdrones.com",
            "logo": "https://cosmikdrones.com/Cosmik%20Logo/Kolonia_Logo.png"
          },
          "knowsAbout": ["Aerial Photography", "FPV Drone Filming", "Cinematic Videography", "Drone Piloting", "FAA Regulations"],
          "alumniOf": [], // Add if applicable, e.g., a flight school
          "memberOf": {
            "@type": "Organization",
            "name": "Greater Lehigh Valley Chamber of Commerce",
            "url": "https://www.lehighvalleychamber.org/"
          },
          "sameAs": [
            "https://www.instagram.com/josh.kolonia/",
            "https://web.lehighvalleychamber.org/Video-Production-and-Drone-Services/Cosmik-Drones-17765"
            // Add LinkedIn profile for Josh if available
          ]
        }}
      />
      <SchemaMarkup 
        type="Organization"
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Cosmik Drones",
          "url": "https://cosmikdrones.com",
          "logo": "https://cosmikdrones.com/Cosmik%20Logo/Kolonia_Logo.png",
          "founder": {
            "@type": "Person",
            "name": "Josh Kolonia",
            "url": "https://cosmikdrones.com/about"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-610-509-0867",
            "contactType": "Customer Service"
          },
          "memberOf": {
            "@type": "Organization",
            "name": "Greater Lehigh Valley Chamber of Commerce",
            "url": "https://www.lehighvalleychamber.org/"
          }
        }}
      />
      
      <div className="section-container">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-lightText mb-6">About Cosmik Drones - Lehigh Valley's Drone Experts</h1>
          <p className="text-xl text-lightText/80 max-w-4xl mx-auto">
            I am an FAA-certified drone specialist serving the Lehigh Valley, Eastern Pennsylvania, and surrounding regions with professional aerial services.
          </p>
        </div>

        {/* Josh Hard at Work Video - Hero Style */}
        <div className="mb-24">
          <div className="aspect-video rounded-lg overflow-hidden">
            <video 
              src="/videos/Josh Hard at Work.mp4" 
              className="w-full h-full object-cover"
              aria-label="Josh Kolonia at work with drones in Lehigh Valley, PA"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            ></video>
          </div>
        </div>

        {/* My Story Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-24">
          <div>
            <h2 className="text-3xl font-bold text-lightText mb-6">My Lehigh Valley Story</h2>
            <p className="text-lg text-lightText/80 mb-4">
              Founded in 2019 by Josh Kolonia, Cosmik Drones began with a simple vision: to capture stunning aerial perspectives throughout the Lehigh Valley that were previously impossible to achieve.
            </p>
            <p className="text-lg text-lightText/80 mb-4">
              What started as a passion project quickly evolved into the Lehigh Valley's premier drone service provider, specializing in cinematic FPV (First Person View) drone filming that puts viewers right in the middle of the action across Allentown, Bethlehem, and Easton.
            </p>
            <p className="text-lg text-lightText/80">
              Today, I combine technical expertise with artistic vision to deliver breathtaking aerial content for clients across Pennsylvania—from real estate and construction to events and film production throughout the region.
            </p>
          </div>
          <div className="relative h-[400px] rounded-lg overflow-hidden">
            <img 
              src="/images/Skirmish Profile.png" 
              alt="Josh Kolonia - Cosmik Drones, Lehigh Valley's Drone Expert" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
        
        {/* Allentown Statue Image */}
        <div className="mb-24">
          <div className="group relative rounded-lg overflow-hidden shadow-lg">
            <img 
              src="/images/Drone/Close Allentown Statue.jpg" 
              alt="Allentown Statue captured by drone" 
              className="w-full h-[600px] object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-darkBg to-transparent opacity-50"></div>
          </div>
        </div>

        {/* My Approach Section */}
        <div className="mb-24">
          <div className="glass-card p-10 rounded-lg">
            <h2 className="text-3xl font-bold text-lightText mb-6 text-center">My Professional Approach</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
              <div className="text-center">
                <div className="bg-primary/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lightText mb-3">Creative Vision</h3>
                <p className="text-lightText/80">
                  I approach each Pennsylvania project with a filmmaker's eye, finding the perfect angles and movements to tell your story effectively and showcase the beauty of the Lehigh Valley region.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lightText mb-3">Technical Expertise</h3>
                <p className="text-lightText/80">
                  I combine years of experience with cutting-edge equipment to achieve shots that others simply can't, overcoming the unique challenges presented by Eastern Pennsylvania's diverse landscapes.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lightText mb-3">Client Collaboration</h3>
                <p className="text-lightText/80">
                  I work closely with Lehigh Valley businesses and individuals to understand your goals and deliver content that exceeds expectations, with local knowledge that makes a difference.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAA Certification Section */}
        <div className="mb-24">
          <div className="glass-card p-10 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl font-bold text-lightText mb-6">FAA Certified for Pennsylvania Operations</h2>
                <p className="text-lg text-lightText/80 mb-6">
                  All my drone operations throughout Lehigh Valley and Pennsylvania are fully compliant with FAA regulations. I hold Part 107 Remote Pilot Certification and maintain comprehensive insurance coverage for your peace of mind.
                </p>
                <ul className="space-y-3 text-lightText/80">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Part 107 certified pilot serving Eastern Pennsylvania</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Comprehensive liability insurance for all Lehigh Valley projects</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Airspace authorization capabilities throughout Pennsylvania</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Safety-first approach to all operations in every location we serve</span>
                  </li>
                </ul>
              </div>
              <div className="relative h-[300px] rounded-lg overflow-hidden">
                <img 
                  src="/images/FAA License.JPG" 
                  alt="FAA Drone Certification for Lehigh Valley Drone Services" 
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
          </div>
      </div>
    </div>
  );
};

export default About; 